import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { IconChevronRight, SkeletonLabel } from '@noths/polaris-client-ribbons-design-system';
import dynamic from 'next/dynamic';

import { DELIVERY_DRAWER_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import type { DecoratedDeliveryOption } from 'src/redux/delivery/types';
import { DeliverySummary } from '../DeliverySummary/DeliverySummary';
import * as styles from './DeliverySection.styles';

const DeliveryAndReturnsDrawer = dynamic(() =>
  import(
    /* webpackChunkName: "delivery_returns_drawer" */ '../DeliveryAndReturnsDrawer/DeliveryAndReturnsDrawer'
  ).then((mod) => mod.DeliveryAndReturnsDrawer),
);

export interface DeliverySectionStateProps {
  deliveryOptions: DecoratedDeliveryOption[] | [];
  loading: boolean;
  partnerName: string;
}

export interface DeliverySectionDispatchProps {
  onDetailsLinkClick?: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
  onVisible: () => void;
}

type DeliverySectionProps = DeliverySectionStateProps & DeliverySectionDispatchProps;

export const DeliverySection = ({
  deliveryOptions,
  loading,
  onDetailsLinkClick,
  onVisible,
  partnerName,
}: DeliverySectionProps) => {
  const deliverySectionRef = useImpressionRef<HTMLDivElement>(() => onVisible());
  const skeletonCount = 2;
  const deliveryOptionsPlaceholderItems = Array.from({ length: skeletonCount });

  return (
    <div ref={deliverySectionRef}>
      {loading ? (
        <div css={styles.skeletonWrapper}>
          {deliveryOptionsPlaceholderItems.map((_, index) => (
            <SkeletonLabel key={`skeleton-${index}`} labelHeight="XL" />
          ))}
        </div>
      ) : (
        <DeliverySummary
          cheapestOption={deliveryOptions[0]}
          numberOfOptions={deliveryOptions.length}
          partnerName={partnerName}
        />
      )}
      <a
        css={styles.drawerLink}
        href={`#${DELIVERY_DRAWER_URL_FRAGMENT_ID}`}
        onClick={onDetailsLinkClick}
      >
        Delivery and returns details{' '}
        <IconChevronRight color="var(--brand-purple-base)" height={16} width={16} />
      </a>
      <DeliveryAndReturnsDrawer target={deliverySectionRef} />
    </div>
  );
};
